import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Option } from 'components/common/types/DropdownItem.types';
import locationSvg from 'assets/icons/location.svg';
import { Store } from 'components/common/types/Store.types';
import { BaseSelectMultiLevel } from 'lib/BaseSelect';
import { LocationItem } from 'components/pages/AvailabilityReport/AddFilter/AddFilter.types';
import { transformLocationData } from './locationUtils';

interface FilterLocationProps {
  selectedLocations?: LocationItem[];
  changeFilters: (filters: { locations: LocationItem[] }) => void;
  isDisabled?: boolean;
}

const FilterLocation: React.FC<FilterLocationProps> = ({
  selectedLocations = [],
  changeFilters,
  isDisabled = false,
}) => {
  const referenceLocationData = useSelector((state: Store) => state.userOptions.referenceOptions.location);
  const isLoading = useSelector((state: Store) => state.dealManagement.isFetchingReferences);

  const locationOptions = useMemo(() => {
    if (isLoading || !referenceLocationData) return [];
    return transformLocationData(referenceLocationData);
  }, [referenceLocationData, isLoading]);

  const selectedLocationCodes = useMemo(() => {
    return selectedLocations.map((location) => location.code);
  }, [selectedLocations]);

  const handleLocationChange = (node: Option, checked: boolean): void => {
    let parentName: string | undefined;
    if (node.parentCode) {
      const parentOption = locationOptions.find((opt) => opt.code === node.parentCode);
      parentName = parentOption?.name;
    }

    const locationItem: LocationItem = {
      code: String(node.code),
      name: node.name,
      parentCode: node.parentCode ? String(node.parentCode) : undefined,
      parentName,
    };

    const updatedLocations = checked
      ? [...selectedLocations, locationItem]
      : selectedLocations.filter((location) => location.code !== locationItem.code);
    changeFilters({ locations: updatedLocations });
  };

  return (
    <BaseSelectMultiLevel
      options={locationOptions}
      value={selectedLocationCodes}
      onChangeItem={handleLocationChange}
      isDisabled={isDisabled || isLoading}
      dataTestId="locations-multi-level-dropdown"
      icon={locationSvg}
      placeholder="Location"
      classNameWrapper="w-52"
      selectedLabel="Location"
    />
  );
};

export default FilterLocation;
